import React from 'react';
import { PerformanceSummary } from './types';

interface AIAnalysisSectionProps {
  performanceSummary: Pick<PerformanceSummary, 
    'ai_overall_assessment' | 
    'ai_study_recommendations' | 
    'recommended_daily_study_minutes' | 
    'recommended_focus_subjects' | 
    'ai_strengths_summary' | 
    'ai_improvement_areas' | 
    'ai_personalized_goals' | 
    'next_milestone' | 
    'ai_predicted_performance' | 
    'recommended_difficulty_progression' | 
    'ai_learning_patterns'
  >;
}

const AIAnalysisSection: React.FC<AIAnalysisSectionProps> = ({ performanceSummary }) => {
  // Helper function to parse JSON fields
  const parseJsonField = (field: string | string[]): string[] => {
    if (Array.isArray(field)) {
      return field;
    }
    try {
      const parsed = JSON.parse(field);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  return (
    <>
      {/* AI Analysis Section */}
      <div className="row mb-4">
        <div className="col-md-6 mb-3">
          <div className="card h-100">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-brain me-2"></i>
                AI Assessment
              </h5>
            </div>
            <div className="card-body">
              <p className="card-text">{performanceSummary.ai_overall_assessment}</p>
            </div>
          </div>
        </div>

        <div className="col-md-6 mb-3">
          <div className="card h-100">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-lightbulb me-2"></i>
                Study Recommendations
              </h5>
            </div>
            <div className="card-body">
              <p className="card-text">{performanceSummary.ai_study_recommendations}</p>
              <div className="mt-3">
                <strong>Recommended Daily Study:</strong>
                <span className="badge bg-primary ms-2">
                  {performanceSummary.recommended_daily_study_minutes} minutes
                </span>
              </div>
              {performanceSummary.recommended_focus_subjects && (
                <div className="mt-2">
                  <strong>Focus Subjects:</strong>
                  <div className="mt-1">
                    {parseJsonField(performanceSummary.recommended_focus_subjects).map((subject, index) => (
                      <span key={index} className="badge bg-info me-1">
                        {subject}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Strengths & Improvement Areas */}
      <div className="row mb-4">
        <div className="col-md-6 mb-3">
          <div className="card h-100">
            <div className="card-header bg-success text-white">
              <h5 className="mb-0">
                <i className="fas fa-star me-2"></i>
                Your Strengths
              </h5>
            </div>
            <div className="card-body">
              <p className="card-text">{performanceSummary.ai_strengths_summary}</p>
            </div>
          </div>
        </div>

        <div className="col-md-6 mb-3">
          <div className="card h-100">
            <div className="card-header bg-warning text-dark">
              <h5 className="mb-0">
                <i className="fas fa-target me-2"></i>
                Improvement Areas
              </h5>
            </div>
            <div className="card-body">
              <p className="card-text">{performanceSummary.ai_improvement_areas}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Learning Goals & Predictions */}
      <div className="row mb-4">
        <div className="col-md-6 mb-3">
          <div className="card h-100">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-flag-checkered me-2"></i>
                Personalized Goals
              </h5>
            </div>
            <div className="card-body">
              <p className="card-text">{performanceSummary.ai_personalized_goals}</p>
              <div className="mt-3">
                <strong>Next Milestone:</strong>
                <br />
                <span className="text-primary">{performanceSummary.next_milestone}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-6 mb-3">
          <div className="card h-100">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-crystal-ball me-2"></i>
                Performance Predictions
              </h5>
            </div>
            <div className="card-body">
              <p className="card-text">{performanceSummary.ai_predicted_performance}</p>
              <div className="mt-3">
                <strong>Difficulty Progression:</strong>
                <br />
                <span className="text-info">{performanceSummary.recommended_difficulty_progression}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Learning Patterns */}
      {performanceSummary.ai_learning_patterns && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="fas fa-chart-pie me-2"></i>
                  Learning Patterns Analysis
                </h5>
              </div>
              <div className="card-body">
                <p className="card-text">{performanceSummary.ai_learning_patterns}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIAnalysisSection;